import { useDispatch, useSelector } from "react-redux";
import { useGetBookedProposalQuery } from "../../api/gms/proposal/proposal-api";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { selectBookedProposalPagination, setPagination } from "../../features/pagination/pagination-slice";
import { options } from "../../data/group-insurance";
import { GetProposalParams } from "../../api/gms/proposal/proposal-api-request";
import { SearchField } from "../../types/type";
import { useEffect } from "react";
import { DropdownOption } from "../../features/auth/auth-state";
import { totalPages, TotalPages } from "../../features/pagination/pagination-inital-state";

export const useBookedProposal = () => {
    
    const dispatch = useDispatch();
    const {
        searchType,
        searchValue,
        page,
        defaultTotalPages,
        isTyping,
        isPageSizeChanging,
        isDebounceDone,
    } = useSelector(selectBookedProposalPagination);

    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name } : null;
    const setSelectedSearchType = ( type: { id: number; name: string } | null ) => {
        if (!type) {
            dispatch(setPagination({ type: 'bookedProposal', value: { searchType: null, searchValue: "", page: 1 }}));
            return;
        }

        const option = options.find((option) => option.id === type.id) ?? null;
        dispatch(setPagination({ type: 'bookedProposal', value: { searchType: option, searchValue: "", page: 1, }}));
    };

    const setSearchValue = (value: string) => {
            dispatch(setPagination({ type: 'bookedProposal', value: {searchValue: value}}));
    };

    const setPage = (value: number) => {
        dispatch(setPagination({type: 'bookedProposal', value: {page: value}}));
    };

    const params: GetProposalParams = { page, per_page: defaultTotalPages.value };
    if (searchType && searchValue.trim()) {
        params[searchType.value as SearchField] = searchValue.trim();
    }
    
    const { 
        data: bookedProposals, 
        error: errorBookedProposals, 
        isError: isErrorBookedProposals, 
        isLoading: isLoadingBookedProposals, 
        refetch: refetchBookedProposals, 
        isFetching: isFetchingBookedProposals 
    } = useGetBookedProposalQuery(params);
    const errorMessageBookedProposals = getErrorMessage(errorBookedProposals);

    /** Handle search debounce */
    useEffect(() => {
        dispatch(setPagination({type: 'bookedProposal', value: { isTyping: true,isDebounceDone: false }}));

        const timer = setTimeout(() => {
            dispatch(setPagination({type: 'bookedProposal', value: { isDebounceDone: true }}));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, dispatch]);

    /** Stop typing state after debounce and API request has finished. */
    useEffect(() => {
        if (isDebounceDone && !isFetchingBookedProposals) {
            dispatch(setPagination({type: 'bookedProposal', value: { isTyping: false }}));
        }
    }, [isDebounceDone, isFetchingBookedProposals, dispatch]);

    /** Stop page-size loading state after the API request has finished. */
    useEffect(() => {
        if (isPageSizeChanging && !isFetchingBookedProposals) {
            dispatch(setPagination({type: 'bookedProposal', value: { isPageSizeChanging: false }}));
        }
    }, [isPageSizeChanging, isFetchingBookedProposals, dispatch]);

    /** Handle page-size change */
    const handleChangePageSize = (value: TotalPages) => {
        dispatch(setPagination({
            type: 'bookedProposal',
            value: {
                defaultTotalPages: value,
                page: 1,
                isPageSizeChanging: true
            }
        }));
    };

    /** Handle reload */
    const handleReload = () => {
        dispatch(setPagination({type: 'bookedProposal', value: {page: 1}}));
        refetchBookedProposals();
    };

    /** Combined loading state */
    const loading = isTyping || isLoadingBookedProposals || isPageSizeChanging;

    const normalizedOptions: DropdownOption[] = options.map(
        (option) => ({id: option.id, name: option.name})
    );

    return {
        bookedProposals,
        errorMessage: errorMessageBookedProposals,
        isErrorBookedProposals,
        isLoadingBookedProposals,
        refetchBookedProposals,
        isFetchingBookedProposals,
        
        normalizedOptions,
        searchType: selectedSearchType,
        setSearchType: setSelectedSearchType,
        searchValue,
        setSearchValue,
        setPage,
        pagedData: bookedProposals?.data ?? [],
        loading,
        handleReload,
        defaultTotalPages,
        setDefaultTotalPages: handleChangePageSize,
        proposalDefaultPageSize: totalPages,
        handleChangePageSize,
    }
}